//@flow
import * as React from 'react';
import i18n from 'app/common/lib/i18n';
import IntellectualPropertyConfirmation from 'app/common/react/components/profile/UserProfileHeader/ModalContents/FlagProfile/IntellectualPropertyConfirmation';
import Modal from 'app/common/react/ui/Modal';
import PolicyViolationConfirmation from 'app/common/react/components/profile/UserProfileHeader/ModalContents/FlagProfile/PolicyViolationConfirmation';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import Toast from 'app/common/react/ui/Toast';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Router } from 'app/common/lib/ReactRouter/types';
import withShowToast from 'app/common/lib/react/withShowToast';
import { compose } from 'redux';
import { Experiment } from 'app/common/react/components/experiments';
import { FLAG_TYPE_KEYS } from 'app/common/react/components/profile/UserProfileHeader/ModalContents/FlagProfile/ReportFlagTypes';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { REASON_KEYS } from 'app/common/react/components/undiscovery/FlagPin/SimplifiedReportPin/ReportReasons';
import { withRouter } from 'react-router';
import { Box, Button, Label, Text, Divider, RadioButton } from 'gestalt';

const REPORT_TYPE_STEP = 'report-type-step';
const REASON_STEP = 'reason-step';
const CONFIRM_STEP = 'confirm-step';

type OwnProps = {|
  blockUser: (id: string) => void,
  isUserBlocked: boolean,
  onClearModal: Function,
  unblockUser: (id: string) => Promise<Object>,
  user: ProfileUser,
  username: string,
|};

type HOCProps = {|
  router: Router,
  showToast: (Toast: Object) => void,
|};

type Props = {|
  ...OwnProps,
  ...HOCProps,
|};

type State = {|
  reason: ?$Values<REASON_KEYS>,
  reportType: ?string,
  showUserBlockedToastText: ?string,
  step: string,
|};

export class ReportProfile extends React.Component<Props, State> {
  state = {
    reason: null,
    reportType: null,
    step: REPORT_TYPE_STEP,
    showUserBlockedToastText: null,
  };

  handleChangeReason = (val: string) => () =>
    this.setState({
      reason: val,
    });

  handleChangeReportType = (val: string) => () =>
    this.setState({
      reportType: val,
    });

  handleExperimentReportSpam = (val: string) => () =>
    this.setState({
      reason: 'spam',
      reportType: val,
    });

  handleCopyrightReportClick = () => {
    const {
      user: { username },
      router,
    } = this.props;
    router.push({
      pathname: `/copyright-reporting/${username}/`,
    });
  };

  handleChangeStep = (step: string) => () => {
    this.setState({ step });
  };

  handleSubmitReport = () => {
    const { onClearModal, username, showToast } = this.props;
    const { reportType, reason } = this.state;

    const resourceOptions = {
      username,
      reason,
      reportObject: reportType,
    };

    ResourceFactory.create('UserReportProfileResource', resourceOptions)
      .callCreate()
      .then(() => {
        onClearModal();
        showToast(({ onHide }) => (
          <Toast text={i18n._('Report sent', 'User profile report was sent')} onHide={onHide} />
        ));
      });
  };

  handleChangeBlocked = (id: string, name: string) => () => {
    const { isUserBlocked, blockUser, unblockUser, user } = this.props;
    const { id } = user;

    if (isUserBlocked) {
      unblockUser(id);
      this.showChangeBlock({ name, showBlocked: false });
    } else {
      blockUser(id);
      this.showChangeBlock({ name, showBlocked: true });
    }
  };

  showChangeBlock = ({ name, showBlocked }: { name: string, showBlocked: boolean }) => {
    const toastText = showBlocked
      ? interpolateNamedTemplate(i18n._('{{ name }} blocked', 'Toast text for blocking user'), {
          name,
        })
      : interpolateNamedTemplate(i18n._('Unblocked {{ name }}', 'Toast text for unblocking user'), {
          name,
        });

    this.setState({
      showUserBlockedToastText: toastText.join(''),
    });
  };

  hideUserBlockedToast = () =>
    this.setState({
      showUserBlockedToastText: null,
    });

  renderFooter() {
    const { onClearModal } = this.props;
    const { reportType, reason, step } = this.state;
    const isNotFinalStep = step !== CONFIRM_STEP;
    const disabled =
      (!reportType && step === REPORT_TYPE_STEP) || (!reason && step === REASON_STEP);
    const showForwardButton = !(
      step === CONFIRM_STEP && reason === REASON_KEYS.INTELLECTUAL_PROPERTY
    );

    const nextButtonText = isNotFinalStep
      ? i18n._('Next', 'Button to progress to next screen in report flow')
      : i18n._('Report', 'Button text to submit a report profile');

    let nextButtonAction;
    let backButtonAction;
    if (step === REPORT_TYPE_STEP) {
      nextButtonAction = this.handleChangeStep(REASON_STEP);
      backButtonAction = onClearModal;
      if (reportType === 'name') {
        nextButtonAction = this.handleChangeStep(CONFIRM_STEP);
      }
      if (reportType === 'BulkDmca') {
        nextButtonAction = this.handleCopyrightReportClick;
      }
    } else if (step === REASON_STEP) {
      nextButtonAction = this.handleChangeStep(CONFIRM_STEP);
      backButtonAction = this.handleChangeStep(REPORT_TYPE_STEP);
    } else {
      nextButtonAction = this.handleSubmitReport;
      backButtonAction = this.handleChangeStep(REASON_STEP);
      if (reportType === 'name') {
        backButtonAction = this.handleChangeStep(REPORT_TYPE_STEP);
      }
    }

    const backButtonText =
      step === REPORT_TYPE_STEP ? i18n._('Cancel', 'Cancel button') : i18n._('Back', 'Back button');

    return (
      <Box display="flex" direction="row" justifyContent="end" marginLeft={-1} marginRight={-1}>
        <Box paddingX={1}>
          <Button inline onClick={backButtonAction} text={backButtonText} />
        </Box>
        {showForwardButton && (
          <Box paddingX={1}>
            <Button
              color="red"
              disabled={disabled}
              inline
              onClick={nextButtonAction}
              text={nextButtonText}
            />
          </Box>
        )}
      </Box>
    );
  }

  renderReportTypes() {
    const { reportType } = this.state;
    const { user } = this.props;
    const cover_media_type = user.profile_cover && user.profile_cover.source;

    const reportsType = [];
    if (cover_media_type === 'video') {
      reportsType.push({
        key: FLAG_TYPE_KEYS.COVER_VIDEO,
        title: i18n._('Inappropriate cover video', 'Cover video of profile'),
      });
    } else if (cover_media_type === 'image') {
      reportsType.push({
        key: FLAG_TYPE_KEYS.COVER_IMAGE,
        title: i18n._('Inappropriate cover image', 'Cover image of profile'),
      });
    }

    return (
      <Box maxHeight={'60vh'} overflow="auto">
        <Box paddingY={2}>
          <Text align="center" color="gray" size="sm">
            {i18n._('Why are you reporting this account?', 'What is the reason for reporting')}
          </Text>
        </Box>
        <Divider />
        <Experiment name="web_creators_bulk_dmca_tool">
          {({ anyEnabled }) =>
            anyEnabled && (
              <React.Fragment>
                <Box key={'Option-ReportSpam'}>
                  <Label htmlFor={'ReportSpam'}>
                    <Box alignItems="center" paddingY={2} display="flex" direction="row">
                      <Box paddingX={4}>
                        <RadioButton
                          checked={reportType === 'name'}
                          id={'ReportSpam'}
                          name="reportPinOptions"
                          onChange={this.handleExperimentReportSpam('name')}
                          value={'ReportSpam'}
                        />
                      </Box>
                      <Box flex="grow">
                        <Box marginBottom={1}>
                          <Text weight="bold">{i18n._('Spam', 'Report user for spam')}</Text>
                        </Box>
                      </Box>
                    </Box>
                  </Label>
                  <Divider />
                </Box>
                <Box key={'Option-BulkDmca'}>
                  <Label htmlFor={'BulkDmca'}>
                    <Box alignItems="center" paddingY={2} display="flex" direction="row">
                      <Box paddingX={4}>
                        <RadioButton
                          checked={reportType === 'BulkDmca'}
                          id={'BulkDmca'}
                          name="reportPinOptions"
                          onChange={this.handleChangeReportType('BulkDmca')}
                          value={'BulkDmca'}
                        />
                      </Box>
                      <Box flex="grow">
                        <Box marginBottom={1}>
                          <Text weight="bold">
                            {i18n._(
                              'Copyright infringement',
                              'Report users pins for copyright infringement',
                            )}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  </Label>
                  <Divider />
                </Box>
              </React.Fragment>
            )
          }
        </Experiment>
        {reportsType.map((opt, idx) => (
          <Box key={`Option-${opt.key}`}>
            <Label htmlFor={opt.key}>
              <Box alignItems="center" paddingY={2} display="flex" direction="row">
                <Box paddingX={4}>
                  <RadioButton
                    checked={opt.key === reportType}
                    id={opt.key}
                    name="reportPinOptions"
                    onChange={this.handleChangeReportType(opt.key)}
                    value={opt.key}
                  />
                </Box>
                <Box flex="grow">
                  <Box marginBottom={1}>
                    <Text weight="bold">{opt.title}</Text>
                  </Box>
                </Box>
              </Box>
            </Label>
            {reportsType.length > idx + 1 && <Divider />}
          </Box>
        ))}
      </Box>
    );
  }

  renderReasons() {
    const { reason } = this.state;

    const reasons = [
      {
        key: REASON_KEYS.SPAM,
        title: i18n._('Spam', 'Report profile option'),
        subtitle: i18n._('Misleading or repetitive posts', 'Report profile option description'),
      },
      {
        key: REASON_KEYS.PORN,
        title: i18n._('Nudity or pornography', 'Report profile option'),
        subtitle: i18n._('Sexually explicit content', 'Report profile option description'),
      },
      {
        key: REASON_KEYS.SELF_INJURY,
        title: i18n._('Self-harm', 'Report profile option'),
        subtitle: i18n._('Eating disorders, cutting, suicide', 'Report profile option description'),
      },
      {
        key: REASON_KEYS.MEDICAL_MISINFORMATION,
        title: i18n._('Medical misinformation', 'Report profile option'),
        subtitle: i18n._(
          'Harmful or misleading content about health and safety',
          'Report profile option description',
        ),
      },
      {
        key: REASON_KEYS.HATE_SPEECH,
        title: i18n._('Hate speech or symbols', 'Report profile option'),
        subtitle: i18n._(
          'Attacks directed at protected groups',
          'Report profile option description',
        ),
      },
      {
        key: REASON_KEYS.HARASSMENT,
        title: i18n._('Harassment or privacy violations', 'Report profile option'),
        subtitle: i18n._(
          'Threats, blackmail, personal attacks',
          'Report profile option description',
        ),
      },
      {
        key: REASON_KEYS.VIOLENCE,
        title: i18n._('Graphic violence', 'Report profile option'),
        subtitle: i18n._(
          'Violent images or promotion of violence',
          'Report profile option description',
        ),
      },
      {
        key: REASON_KEYS.INTELLECTUAL_PROPERTY,
        title: i18n._('My intellectual property', 'Report profile option'),
        subtitle: i18n._(
          'Copyright or trademark infringement',
          'Report profile option description',
        ),
      },
    ];

    return (
      <Box maxHeight={'60vh'} overflow="auto">
        {reasons.map((opt, idx) => (
          <Box key={`Option-${opt.key}`}>
            <Label htmlFor={opt.key}>
              <Box alignItems="center" paddingY={2} display="flex" direction="row">
                <Box paddingX={4}>
                  <RadioButton
                    checked={opt.key === reason}
                    id={opt.key}
                    name="reportPinOptions"
                    onChange={this.handleChangeReason(opt.key)}
                    value={opt.key}
                  />
                </Box>
                <Box flex="grow">
                  <Box marginBottom={1}>
                    <Text weight="bold">{opt.title}</Text>
                  </Box>
                  <Text size="sm">{opt.subtitle}</Text>
                </Box>
              </Box>
            </Label>
            {reasons.length > idx + 1 && <Divider />}
          </Box>
        ))}
      </Box>
    );
  }

  render() {
    const { onClearModal, user, isUserBlocked } = this.props;
    const { step, reason, showUserBlockedToastText } = this.state;
    const reportProfileHeading = i18n._('Report account', 'Modal heading for report profile flow');

    let renderContent;
    if (step === REPORT_TYPE_STEP) {
      renderContent = this.renderReportTypes();
    } else if (step === REASON_STEP) {
      renderContent = this.renderReasons();
    } else if (step === CONFIRM_STEP && reason === REASON_KEYS.INTELLECTUAL_PROPERTY) {
      renderContent = <IntellectualPropertyConfirmation onDismiss={onClearModal} />;
    } else {
      renderContent = (
        <PolicyViolationConfirmation
          reason={reason}
          user={user}
          userBlocked={isUserBlocked}
          onChangeBlocked={this.handleChangeBlocked}
        />
      );
    }

    return (
      <Modal
        accessibilityCloseLabel={i18n._('Close', 'Modal close label')}
        accessibilityModalLabel={reportProfileHeading}
        onDismiss={onClearModal}
        heading={reportProfileHeading}
        footer={this.renderFooter()}
      >
        {renderContent}
        <Box
          fit
          dangerouslySetInlineStyle={{
            __style: { bottom: '15%', left: '50%', transform: 'translateX(-50%)' },
          }}
          paddingX={1}
          position="fixed"
        >
          {showUserBlockedToastText && (
            <Toast
              duration={2000}
              onHide={this.hideUserBlockedToast}
              text={showUserBlockedToastText}
            />
          )}
        </Box>
      </Modal>
    );
  }
}

export default compose(
  withShowToast,
  withRouter,
)(ReportProfile);
